<template>
  <main class="account-page">
    <div class="container">
      <section class="profile-card">
        <div class="card-heading">
          <span class="material-icons profile-icon">account_circle</span>
          <div>
            <h1>{{ $t('accountPage.profile') }}</h1>
            <p>{{ $t('accountPage.profileDescription') }}</p>
          </div>
        </div>

        <p v-if="isLoading"
           class="status-message"
        >
          {{ $t('accountPage.loading') }}
        </p>

        <form v-else
              class="profile-form"
              @submit.prevent="saveProfile"
        >
          <div class="help-field">
            <MainInput :text="name"
                       type="text"
                       name="name"
                       :placeholder="$t('accountPage.name')"
                       :is-readonly="true"
                       @value-changed="name = $event.target.value"
            />
            <button type="button"
                    class="help-button"
                    :aria-label="$t('accountPage.readOnlyHelp', { supportEmail })"
            >
              ?
              <span class="help-tooltip"
                    role="tooltip"
              >{{ $t('accountPage.readOnlyHelp', { supportEmail }) }}</span>
            </button>
          </div>
          <div class="help-field">
            <MainInput :text="email"
                       type="text"
                       name="email"
                       :placeholder="$t('accountPage.email')"
                       :is-readonly="true"
                       @value-changed="email = $event.target.value"
            />
            <button type="button"
                    class="help-button"
                    :aria-label="$t('accountPage.readOnlyHelp', { supportEmail })"
            >
              ?
              <span class="help-tooltip"
                    role="tooltip"
              >{{ $t('accountPage.readOnlyHelp', { supportEmail }) }}</span>
            </button>
          </div>
          <MainInput :text="phone"
                     type="text"
                     name="phone"
                     :placeholder="$t('accountPage.phone')"
                     @value-changed="phone = $event.target.value"
          />
          <MainInput type="select"
                     name="language"
                     :selected-item="language"
                     :options="languageOptions"
                     :placeholder="$t('accountPage.language')"
                     @value-changed="language = $event"
          />

          <div class="read-only-section">
            <h2>{{ $t('accountPage.customers') }}</h2>
            <p v-if="isReadOnly"
               class="access-note"
            >
              {{ $t('accountPage.readOnlyAccess') }}
            </p>
            <p v-if="hasAllCustomers">{{ $t('accountPage.allCustomers') }}</p>
            <ul v-else-if="customers.length">
              <li v-for="customer in customers"
                  :key="customer.Id"
              >
                {{ customer.Name }}
              </li>
            </ul>
            <p v-else>{{ $t('accountPage.noCustomers') }}</p>
          </div>

          <MainButton :button-text="isSaving ? $t('accountPage.saving') : $t('accountPage.save')"
                      type="submit"
                      style="height: 50px; margin-top: 1.5rem;"
          />
        </form>
      </section>
    </div>
  </main>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import { useStore } from 'vuex'
import { useToast } from 'vue-toastification'
import MainButton from '@/components/buttons/MainButton.vue'
import MainInput from '@/components/inputs/MainInput.vue'
import i18n from '@/locales/i18n'

const store = useStore()
const toast = useToast()
const t = i18n.global.t

const name = ref('')
const email = ref('')
const phone = ref('')
const language = ref('Swedish')
const isLoading = ref(true)
const isSaving = ref(false)
const supportEmail = 'support@nordomatic.com'

const languagesByLocale = {
  sv: 'Swedish',
  en: 'English',
  no: 'Norwegian',
  dk: 'Danish'
}

const localeByLanguage = {
  Swedish: 'sv',
  English: 'en',
  Norwegian: 'no',
  Danish: 'dk'
}

const languageOptions = computed(() => ({
  Swedish: t('accountPage.languages.swedish'),
  English: t('accountPage.languages.english'),
  Norwegian: t('accountPage.languages.norwegian'),
  Danish: t('accountPage.languages.danish')
}))

const customers = computed(() => store.state.account.me.Customers || [])
const hasAllCustomers = computed(() => store.getters['account/hasAllCustomers'])
const isReadOnly = computed(() => store.getters['account/isReadOnly'])

onMounted(async () => {
  try {
    const user = await store.dispatch('account/getMe')
    name.value = user.Name || ''
    email.value = user.Email || ''
    phone.value = user.Phone || ''
    language.value = languagesByLocale[store.state.account.language] || 'Swedish'
  } catch (error) {
    toast.error(t('accountPage.loadFailed'))
  } finally {
    isLoading.value = false
  }
})

async function saveProfile () {
  if (isSaving.value) {
    return
  }

  isSaving.value = true
  try {
    await store.dispatch('account/updateMe', {
      Name: name.value,
      Email: email.value,
      Language: language.value,
      Phone: phone.value
    })
    i18n.global.locale = localeByLanguage[language.value]
    toast.success(t('accountPage.saveSuccess'))
  } catch (error) {
    toast.error(t('accountPage.saveFailed'))
  } finally {
    isSaving.value = false
  }
}
</script>

<style lang="scss" scoped>
.account-page {
  height: 100%;
  overflow-y: auto;
  background: linear-gradient(90deg, var(--gray) 0%, var(--background2) 10px);
}

.container {
  width: min(620px, calc(100% - 2rem));
  margin: 0 auto;
  padding: 4rem 0;
}

.profile-card {
  padding: 2rem;
  color: white;
  background: var(--mainBlue);
  border-radius: 20px;
}

.card-heading {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;

  h1 {
    font-size: 1.6rem;
  }

  p {
    margin-top: 0.25rem;
    color: var(--mainLightBlue);
  }
}

.profile-icon {
  font-size: 3rem;
  color: var(--mainLightBlue);
}

.status-message {
  padding: 2rem 0;
  text-align: center;
}

.help-field {
  position: relative;
}

.help-button {
  position: absolute;
  top: 17px;
  right: 16px;
  z-index: 2;
  width: 24px;
  height: 24px;
  font-weight: 700;
  color: #94a3b8;
  background: transparent;
  border-radius: 50%;

  &:hover,
  &:focus {
    color: #64748b;
  }

  &:focus-visible {
    outline: 2px solid var(--mainBlue);
    outline-offset: 2px;
  }

  &:hover .help-tooltip,
  &:focus .help-tooltip {
    visibility: visible;
    opacity: 1;
  }
}

.help-tooltip {
  position: absolute;
  right: -8px;
  bottom: calc(100% + 8px);
  width: 260px;
  padding: 0.65rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 400;
  line-height: 1.35;
  color: #1f2937;
  text-align: left;
  pointer-events: none;
  visibility: hidden;
  background: white;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.2);
  opacity: 0;
  transition: opacity 0.15s ease;
}

.read-only-section {
  padding: 1rem;
  margin-top: 0.5rem;
  color: #1f2937;
  background: whitesmoke;
  border-radius: 5px 15px;

  h2 {
    margin-bottom: 0.5rem;
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--mainBlue);
  }

  ul {
    padding-left: 1.25rem;
  }

  li + li {
    margin-top: 0.35rem;
  }

  .access-note {
    margin-bottom: 0.5rem;
    font-style: italic;
  }
}

@media (max-width: 768px) {
  .container {
    padding: 2rem 0;
  }

  .profile-card {
    padding: 1.25rem;
  }
}
</style>
